'use client';

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getNoteById, createNote, updateNote } from '@/services/supabase/notes';
import { PlateNoteEditor } from '@/components/editor/plate-note-editor';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useAutoSave, restoreAutoSavedContent, clearAutoSavedContentAfterSave } from '@/hooks/useAutoSave';

// Simple spinner component
function Spinner({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const sizeClass =
    size === "sm" ? "h-4 w-4" :
    size === "lg" ? "h-8 w-8" :
    "h-6 w-6";

  return (
    <div className="flex items-center justify-center">
      <div className={`animate-spin ${sizeClass} text-primary`} role="status">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    </div>
  );
}

// Clean content for storage
const cleanContentForStorage = (content: any) => {
  if (!content) return [];

  try {
    // Create a replacer function to handle circular references
    const getCircularReplacer = () => {
      const seen = new WeakSet();
      return (key: string, value: any) => {
        // Skip React-specific properties and functions
        if (key.startsWith('__') ||
            key === 'ref' ||
            key === 'key' ||
            key === 'window' ||
            key === 'document' ||
            key === 'parent' ||
            key === 'self' ||
            typeof value === 'function' ||
            value instanceof Node) {
          return undefined;
        }

        // Handle circular references for objects
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return undefined; // Skip circular reference
          }
          seen.add(value);
        }

        return value;
      };
    };

    // Convert to plain object without circular references
    return JSON.parse(JSON.stringify(content, getCircularReplacer()));
  } catch (e) {
    console.error('Error cleaning content:', e);
    // Return a safe fallback
    return [{ type: 'p', children: [{ text: '' }] }];
  }
};

export default function NotePage() {
  // Get note ID from URL parameters
  const params = useParams();
  const noteIdParam = params.noteId || '';

  // State for the note
  const [isLoading, setIsLoading] = useState(!!noteIdParam);
  const [error, setError] = useState<string | null>(null);
  const [currentNoteId, setCurrentNoteId] = useState<string | null>(noteIdParam || null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState<any[]>([
    { type: 'h1', children: [{ text: 'Untitled Note' }] },
    { type: 'p', children: [{ text: '' }] },
  ]);

  // Auto-save functionality
  const autoSave = useAutoSave({
    content,
    id: currentNoteId || '',
    type: 'note',
    enabled: !!currentNoteId,
    onAutoSave: (data) => {
      console.log('Note content auto-saved:', data.timestamp);
    }
  });

  // Load note data on component mount
  useEffect(() => {
    let isMounted = true;

    // Create a new note if no ID is provided
    if (!noteIdParam) {
      if (isMounted) {
        setTitle('Untitled Note');
        setContent([
          { type: 'h1', children: [{ text: 'Untitled Note' }] },
          { type: 'p', children: [{ text: '' }] },
        ]);
        setIsLoading(false);
        setError(null);
        setCurrentNoteId(null);
      }
      return;
    }

    // Otherwise load the existing note
    async function loadNote() {
      if (isMounted) {
        setIsLoading(true);
      }

      try {
        // Use type assertion to satisfy the type checker
        const noteData = await getNoteById(noteIdParam as string);

        if (isMounted && noteData) {
          setTitle(noteData.title || 'Untitled Note');

          // Handle content parsing
          let finalContent;
          if (Array.isArray(noteData.content) && noteData.content.length > 0) {
            // If there's existing content, ensure it has an H1 at the beginning
            finalContent = [...noteData.content];

            // Check if the first element is an H1
            if (finalContent[0]?.type !== 'h1') {
              // If not, add an H1 with the note title at the beginning
              finalContent.unshift({
                type: 'h1',
                children: [{ text: noteData.title || 'Untitled Note' }]
              });
            }
          } else if (typeof noteData.content === 'string') {
            try {
              const parsedContent = JSON.parse(noteData.content);

              if (Array.isArray(parsedContent) && parsedContent.length > 0) {
                // Ensure content has an H1
                finalContent = [...parsedContent];

                if (finalContent[0]?.type !== 'h1') {
                  finalContent.unshift({
                    type: 'h1',
                    children: [{ text: noteData.title || 'Untitled Note' }]
                  });
                }
              } else {
                finalContent = [
                  { type: 'h1', children: [{ text: noteData.title || 'Untitled Note' }] },
                  { type: 'p', children: [{ text: '' }] },
                ];
              }
            } catch (e) {
              finalContent = [
                { type: 'h1', children: [{ text: noteData.title || 'Untitled Note' }] },
                { type: 'p', children: [{ text: '' }] },
              ];
            }
          } else {
            finalContent = [
              { type: 'h1', children: [{ text: noteData.title || 'Untitled Note' }] },
              { type: 'p', children: [{ text: '' }] },
            ];
          }

          // Check for auto-saved content
          const autoSavedData = restoreAutoSavedContent(noteIdParam, 'note');
          if (autoSavedData) {
            // Compare timestamps to determine which content is newer
            const dbTimestamp = noteData.updated_at ? new Date(noteData.updated_at).getTime() : 0;
            const autoSaveTimestamp = new Date(autoSavedData.timestamp).getTime();

            if (autoSaveTimestamp > dbTimestamp) {
              console.log('Using auto-saved note content (newer than database)');
              finalContent = autoSavedData.content;
            } else {
              console.log('Database note content is newer, clearing auto-saved content');
              clearAutoSavedContentAfterSave(noteIdParam, 'note');
            }
          }

          setContent(finalContent);
          setCurrentNoteId(noteIdParam);
        }
      } catch (err) {
        console.error('Error loading note:', err);
        if (isMounted) {
          setError('Failed to load note. Please try again.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadNote();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [noteIdParam]);

  // Handle title changes - extracted from the H1 in the editor
  const handleTitleChange = async (newTitle: string) => {
    // Only update the local state, don't save to the database
    // This prevents API calls on every keystroke
    setTitle(newTitle);

    // Title will be saved along with content when the save button is clicked
  };

  // Handle content changes
  const handleContentChange = async (newContent: any) => {
    try {
      // Clean the content to remove circular references
      const cleanedContent = cleanContentForStorage(newContent);

      // Log the content for debugging
      console.log('Content changed in NotePage:', cleanedContent);

      // Store the current content in local state
      setContent(cleanedContent);

      // Extract the title from the first heading
      if (cleanedContent && Array.isArray(cleanedContent) && cleanedContent.length > 0) {
        const firstHeading = cleanedContent.find((node: any) => node.type === 'h1');
        if (firstHeading && firstHeading.children && firstHeading.children.length > 0) {
          const headingText = firstHeading.children.map((child: any) => child.text || '').join('');
          if (headingText && headingText !== title) {
            setTitle(headingText);
          }
        }
      }

      // No auto-save on content change - saving only happens via the save button
      // This prevents API calls on every keystroke
    } catch (error) {
      console.error('Error processing content:', error);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Handle save operation for the note
  const handleSaveNote = async (noteContent: any) => {
    try {
      // Log the incoming content from the editor
      console.log('Incoming content from editor:', noteContent);
      console.log('Current state content:', content);

      // Always use the latest content
      // If the incoming content from the editor has meaningful changes, use that
      // Otherwise, fall back to the state content
      const contentToSave = noteContent && noteContent.length > 0 ? noteContent : content;

      // Clean the content to remove circular references
      const cleanedContent = cleanContentForStorage(contentToSave);

      // Log the final content being saved
      console.log('Final content being saved:', cleanedContent);

      if (currentNoteId) {
        // Update existing note
        await updateNote(Number(currentNoteId), {
          content: cleanedContent,
          title: title // Use the current title
        });

        // Clear auto-saved content after successful save
        clearAutoSavedContentAfterSave(currentNoteId, 'note');

        // Show success toast
        toast({
          title: "Note Saved",
          description: "Your note has been saved successfully.",
          duration: 3000,
        });
      } else if (content && content.length > 0) {
        // Create new note if there's meaningful content
        const hasMeaningfulContent = content.some((node: any) =>
          node.children &&
          node.children.some((child: any) =>
            child.text &&
            child.text.trim() !== '' &&
            // Don't count the default heading as meaningful content
            !(node.type === 'h1' && child.text === 'Untitled Note')
          )
        );

        if (hasMeaningfulContent) {
          // Extract title from first heading if available
          let noteTitle = 'Untitled Note';
          const firstHeading = content.find((node: any) => node.type === 'h1');
          if (firstHeading && firstHeading.children && firstHeading.children.length > 0) {
            const headingText = firstHeading.children.map((child: any) => child.text || '').join('');
            if (headingText.trim()) {
              noteTitle = headingText;
            }
          }

          // Create the new note
          const newNote = await createNote(noteTitle, content);
          setCurrentNoteId(String(newNote.id));

          // Clear auto-saved content after successful save
          clearAutoSavedContentAfterSave(String(newNote.id), 'note');

          // Show success toast
          toast({
            title: "Note Created",
            description: "Your new note has been saved.",
            duration: 3000,
          });
        }
      }
    } catch (error) {
      console.error('Error saving note:', error);
      toast({
        title: "Error",
        description: "Failed to save note. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Render editor
  return (
    <div className="h-full w-full">
      <PlateNoteEditor
        noteId={currentNoteId}
        title={title}
        content={content}
        onTitleChange={handleTitleChange}
        onContentChange={handleContentChange}
        onSave={handleSaveNote}
        autoSaveData={autoSave}
      />
    </div>
  );
}